import type React from "react";
import { ExclamationIcon } from "@heroicons/react/outline";

import { SpanStatus } from "../../utils/calculateSpanStatus";
import { formatDurationLabel, getSpanDisplayName, getSpanDisplayType } from "../utils";

import { Badge, BadgeSize, BadgeVariant } from "@/components/ui/Badge";
import { SpanNode } from "@/types/spans";
import LoadingSpinner from "@/components/LoadingSpinner";

interface SpanTooltipProps {
  span: SpanNode;
  spanDuration: number;
  status: SpanStatus;
}

export const SpanTooltip = ({ span, spanDuration, status }: SpanTooltipProps) => {
  const statusMessage = span.status?.message;
  const startTime = new Date(span.start).toLocaleString();
  const endTime = span.end ? new Date(span.end).toLocaleString() : "Running";
  const nodeType = getSpanDisplayType(span);
  const displayName = getSpanDisplayName(span);

  let statusDisplay: React.ReactNode;
  if (status === SpanStatus.Error) {
    statusDisplay = (
      <Badge variant={BadgeVariant.RED} size={BadgeSize.XS}>
        <ExclamationIcon className="h-3 w-3" />
        Error
      </Badge>
    );
  } else if (status === SpanStatus.Failed) {
    statusDisplay = (
      <Badge variant={BadgeVariant.RED} size={BadgeSize.XS}>
        <ExclamationIcon className="h-3 w-3" />
        Failed
      </Badge>
    );
  } else if (status === SpanStatus.PartiallyFailed) {
    statusDisplay = (
      <Badge variant={BadgeVariant.ORANGE} size={BadgeSize.XS}>
        <ExclamationIcon className="h-3 w-3" />
        Partially Failed
      </Badge>
    );
  } else if (status === SpanStatus.Running) {
    statusDisplay = (
      <Badge variant={BadgeVariant.BLUE} size={BadgeSize.XS} animate>
        <LoadingSpinner size={3} />
        Running
      </Badge>
    );
  } else {
    statusDisplay = (
      <Badge variant={BadgeVariant.GREEN} size={BadgeSize.XS}>
        Success
      </Badge>
    );
  }

  const nodeTypeDisplay = nodeType
    ? nodeType
        .toLowerCase()
        .replace(/_/g, " ")
        .split(" ")
        .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")
    : undefined;

  return (
    <div className="space-y-1.5">
      <div className="font-semibold text-gray-900">{displayName}</div>
      <div className="border-t border-gray-300 pt-1.5">
        <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
          <span className="text-gray-600">Duration:</span>
          <span className="text-gray-900">{formatDurationLabel(spanDuration)}</span>
          {nodeTypeDisplay && (
            <>
              <span className="text-gray-600">Type:</span>
              <span className="text-gray-900">{nodeTypeDisplay}</span>
            </>
          )}
          <span className="text-gray-600">Status:</span>
          <span>{statusDisplay}</span>
          {statusMessage && (
            <>
              <span className="text-gray-600">Message:</span>
              <span className="break-words text-gray-900">{statusMessage}</span>
            </>
          )}
          <span className="text-gray-600">Start:</span>
          <span className="text-xs text-gray-900">{startTime}</span>
          {span.end && (
            <>
              <span className="text-gray-600">End:</span>
              <span className="text-xs text-gray-900">{endTime}</span>
            </>
          )}
          <span className="text-gray-600">Span ID:</span>
          <span className="break-all font-mono text-xs text-gray-900">{span.span_id}</span>
        </div>
      </div>
      {span.events && span.events.length > 0 && (
        <div className="border-t border-gray-300 pt-1.5">
          <div className="text-xs">
            <span className="text-gray-600">Events: </span>
            <span className="text-gray-900">{span.events.length}</span>
          </div>
        </div>
      )}
    </div>
  );
};
